import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { IdCard } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Modal } from '@/components/common/Modal';
import { SelectField, TextField } from '@/components/forms/Field';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { FilterSelect, ListPage } from '@/components/tables/ListPage';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useTableQuery } from '@/hooks/useTableQuery';
import { transportDriversApi } from '@/services/api/endpoints/transport';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import { ROUTES } from '@/app/router/routes';
import type {
  DriverRole,
  DriverStatus,
  LicenseType,
  TrainingStatus,
  TransportDriver,
} from '@/types/transport';
import { formatDate, orDash } from '@/utils/format';
import { EmployeePicker, type PickedEmployee } from '@/features/hr/EmployeePicker';
import { Badge, ComplianceBadge, DriverStatusBadge, humanise } from './shared';

const STATUSES: { value: DriverStatus; label: string }[] = [
  { value: 'ACTIVE', label: 'On duty' },
  { value: 'ON_LEAVE', label: 'On leave' },
  { value: 'SUSPENDED', label: 'Suspended' },
  { value: 'INACTIVE', label: 'Off the roster' },
];

const ROLES: { value: DriverRole; label: string }[] = [
  { value: 'DRIVER', label: 'Drivers' },
  { value: 'ATTENDANT', label: 'Attendants' },
];

const LICENSE_TYPES: LicenseType[] = ['LTV', 'HTV', 'PSV', 'MOTORCYCLE', 'OTHER'];

const TRAINING_STATUSES: TrainingStatus[] = ['PENDING', 'IN_PROGRESS', 'COMPLETED', 'EXPIRED'];

/**
 * Who transport trusts on a route.
 *
 * <p>A profile points at an employee already registered in HR — the person is
 * never duplicated. This row holds only what HR has no column for: the
 * licence, the medical, and whether transport currently trusts them on a
 * trip. When HR renames somebody, the profile follows.
 *
 * <p>Eligibility is the server's verdict, shown as given. A driver registered
 * without a licence is ineligible rather than refused — the profile exists so
 * the gap has somewhere to be seen.
 */
export function DriversPage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const table = useTableQuery('employeeName,asc');
  const manages = can(PERMISSIONS.TRANSPORT_DRIVER_MANAGE);

  const [status, setStatus] = useState<DriverStatus | ''>('');
  const [role, setRole] = useState<DriverRole | ''>('');
  const [registering, setRegistering] = useState(false);

  const [employee, setEmployee] = useState<PickedEmployee | null>(null);
  const [newRole, setNewRole] = useState<DriverRole>('DRIVER');
  const [licenseNumber, setLicenseNumber] = useState('');
  const [licenseType, setLicenseType] = useState<LicenseType | ''>('');
  const [licenseIssueDate, setLicenseIssueDate] = useState('');
  const [licenseExpiryDate, setLicenseExpiryDate] = useState('');
  const [medicalCheckDate, setMedicalCheckDate] = useState('');
  const [medicalExpiryDate, setMedicalExpiryDate] = useState('');
  const [trainingStatus, setTrainingStatus] = useState<TrainingStatus | ''>('');
  const [notes, setNotes] = useState('');

  const params = {
    ...table.params,
    ...(status === '' ? {} : { status }),
    ...(role === '' ? {} : { role }),
  };

  const drivers = useQuery({
    queryKey: queryKeys.transportDrivers.list(params),
    queryFn: () => transportDriversApi.list(params),
  });

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: queryKeys.transportDrivers.all });

  const register = useMutation({
    mutationFn: () =>
      transportDriversApi.create({
        employeeId: employee!.id,
        driverRole: newRole,
        licenseNumber: licenseNumber.trim() === '' ? undefined : licenseNumber.trim(),
        licenseType: licenseType === '' ? undefined : licenseType,
        licenseIssueDate: licenseIssueDate === '' ? undefined : licenseIssueDate,
        licenseExpiryDate: licenseExpiryDate === '' ? undefined : licenseExpiryDate,
        medicalCheckDate: medicalCheckDate === '' ? undefined : medicalCheckDate,
        medicalExpiryDate: medicalExpiryDate === '' ? undefined : medicalExpiryDate,
        trainingStatus: trainingStatus === '' ? undefined : trainingStatus,
        notes: notes.trim() === '' ? undefined : notes.trim(),
      }),
    onSuccess: (driver) => {
      toast.success(`${driver.employeeName} registered as ${humanise(driver.driverRole)}`);
      setRegistering(false);
      setEmployee(null);
      setNewRole('DRIVER');
      setLicenseNumber('');
      setLicenseType('');
      setLicenseIssueDate('');
      setLicenseExpiryDate('');
      setMedicalCheckDate('');
      setMedicalExpiryDate('');
      setTrainingStatus('');
      setNotes('');
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const changeStatus = useMutation({
    mutationFn: ({ id, next, why }: { id: string; next: DriverStatus; why: string }) =>
      transportDriversApi.setStatus(id, next, why),
    onSuccess: (driver) => {
      toast.success(`${driver.employeeName} is now ${humanise(driver.status)}`);
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const columns: Column<TransportDriver>[] = [
    {
      key: 'employeeName',
      header: 'Person',
      sortKey: 'employeeName',
      render: (row) => (
        <span>
          <Link
            to={ROUTES.transportDriverDetail(row.id)}
            className="block text-sm font-semibold text-ink underline-offset-2 hover:text-accent hover:underline"
          >
            {row.employeeName}
          </Link>
          <span className="block font-mono text-[11px] text-subtle">
            {orDash(row.employeeNumber)}
          </span>
        </span>
      ),
    },
    { key: 'driverRole', header: 'Role', render: (row) => humanise(row.driverRole) },
    {
      key: 'licence',
      header: 'Licence',
      sortKey: 'licenseExpiryDate',
      render: (row) => (
        <span>
          <ComplianceBadge state={row.licenseState} />
          <span className="block font-mono text-[11px] text-subtle">
            {orDash(row.licenseNumber)}
            {row.licenseExpiryDate !== undefined && ` · ${formatDate(row.licenseExpiryDate)}`}
          </span>
        </span>
      ),
    },
    {
      key: 'medical',
      header: 'Medical',
      render: (row) => (
        <span>
          <ComplianceBadge state={row.medicalState} />
          {row.medicalExpiryDate !== undefined && (
            <span className="block text-[11px] text-subtle">
              {formatDate(row.medicalExpiryDate)}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'trips',
      header: 'Trips',
      render: (row) =>
        row.tripEligible ? (
          <Badge tone="success">eligible</Badge>
        ) : (
          <span>
            <Badge tone="danger">blocked</Badge>
            <span className="block text-[11px] text-subtle">
              {row.eligibilityBlockers.length} reason
              {row.eligibilityBlockers.length === 1 ? '' : 's'}
            </span>
          </span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      sortKey: 'status',
      render: (row) => <DriverStatusBadge status={row.status} />,
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (row) =>
        manages && (row.status === 'ACTIVE' || row.status === 'SUSPENDED') ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              const next: DriverStatus = row.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE';
              const why = window.prompt(
                next === 'SUSPENDED'
                  ? 'Why are they being suspended? Routes they serve will need cover.'
                  : 'Why are they back on duty?',
              );
              if (why !== null && why.trim() !== '') {
                changeStatus.mutate({ id: row.id, next, why: why.trim() });
              }
            }}
          >
            {row.status === 'ACTIVE' ? 'Suspend' : 'Reinstate'}
          </Button>
        ) : null,
    },
  ];

  return (
    <>
      <ListPage
        title="Drivers"
        description="Drivers and attendants, and the papers that keep them on the road."
        icon={<IdCard className="h-5 w-5" aria-hidden="true" />}
        action={
          manages ? (
            <Button onClick={() => setRegistering(true)}>Register driver</Button>
          ) : undefined
        }
        search={{
          value: table.searchInput,
          onChange: table.setSearchInput,
          placeholder: 'Name, number or licence…',
        }}
        filters={
          <>
            <FilterSelect
              label="Role"
              value={role}
              onChange={(value) => {
                setRole(value);
                table.setPage(0);
              }}
              options={ROLES}
            />
            <FilterSelect
              label="Status"
              value={status}
              onChange={(value) => {
                setStatus(value);
                table.setPage(0);
              }}
              options={STATUSES}
            />
          </>
        }
        pagination={drivers.data?.pagination}
        onPageChange={table.setPage}
        onSizeChange={table.setSize}
      >
        <DataTable
          columns={columns}
          rows={drivers.data?.data}
          rowKey={(row) => row.id}
          loading={drivers.isPending}
          error={drivers.error}
          onRetry={() => void drivers.refetch()}
          sort={table.sort}
          onSort={table.toggleSort}
          emptyTitle="Nobody is registered"
          emptyDescription="An employee has to be given a transport profile before they can be put on a route."
        />
      </ListPage>

      {registering && (
        <Modal
          open
          onClose={() => setRegistering(false)}
          title="Register a driver or attendant"
          description="Points at an existing employee — the person stays in the staff module."
        >
          <form
            className="space-y-3"
            onSubmit={(event) => {
              event.preventDefault();
              register.mutate();
            }}
          >
            <EmployeePicker value={employee} onChange={setEmployee} />

            <SelectField
              label="Role"
              required
              value={newRole}
              onChange={(event) => setNewRole(event.target.value as DriverRole)}
            >
              <option value="DRIVER">Driver</option>
              <option value="ATTENDANT">Attendant</option>
            </SelectField>

            <div className="grid gap-3 sm:grid-cols-2">
              <TextField
                label="Licence number"
                value={licenseNumber}
                onChange={(event) => setLicenseNumber(event.target.value)}
                hint="A driver without one is registered ineligible, not refused"
              />
              <SelectField
                label="Licence type"
                value={licenseType}
                onChange={(event) => setLicenseType(event.target.value as LicenseType | '')}
              >
                <option value="">Not recorded</option>
                {LICENSE_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </SelectField>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <TextField
                label="Licence issued"
                type="date"
                value={licenseIssueDate}
                onChange={(event) => setLicenseIssueDate(event.target.value)}
              />
              <TextField
                label="Licence expires"
                type="date"
                value={licenseExpiryDate}
                onChange={(event) => setLicenseExpiryDate(event.target.value)}
              />
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <TextField
                label="Medical check"
                type="date"
                value={medicalCheckDate}
                onChange={(event) => setMedicalCheckDate(event.target.value)}
              />
              <TextField
                label="Medical expires"
                type="date"
                value={medicalExpiryDate}
                onChange={(event) => setMedicalExpiryDate(event.target.value)}
              />
            </div>

            <SelectField
              label="Training"
              value={trainingStatus}
              onChange={(event) => setTrainingStatus(event.target.value as TrainingStatus | '')}
              hint="Left alone, a new profile starts as pending"
            >
              <option value="">Pending</option>
              {TRAINING_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {humanise(value)}
                </option>
              ))}
            </SelectField>

            <TextField
              label="Notes"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="Anything the roster should know…"
            />

            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="ghost" onClick={() => setRegistering(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={employee === null || register.isPending}>
                {register.isPending ? 'Registering…' : 'Register'}
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
